<template>
  <div class="remaining-leaves-report">
    <!-- Control Buttons -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ modalTitle }} Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ leavesData.length }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
      <p class="mt-3 font-weight-bold">Loading staff data...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">{{ modalTitle }} Report</span>
              <span class="date-range-text">
                Generated On: <strong>{{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Summary -->
      <div class="balance-summary mb-1">
        <div class="balance-card">
          <span class="label">Total Records:</span>
          <span class="value text-primary">{{ leavesData.length }}</span>
        </div>
        <div class="balance-card" v-if="modalTitle === 'Leave Encashment'">
          <span class="label">Total Encashment:</span>
          <span class="value text-success">{{
            formatCurrency(totalEncashment)
          }}</span>
        </div>
      </div>

      <!-- Report Table -->
      <div class="table-container">
        <table class="report-table">
          <thead>
            <tr class="main-header">
              <th rowspan="2" class="text-center" style="width: 5%">S.NO</th>
              <th rowspan="2" class="text-center" style="width: 8%">RFID</th>
              <th rowspan="2" style="width: 15%">STAFF NAME</th>
              <th rowspan="2" style="width: 15%">FATHER NAME</th>
              <th rowspan="2" style="width: 12%">DESIGNATION</th>

              <template
                v-if="
                  modalTitle === 'Remaining Leaves' ||
                  modalTitle === 'Leave Encashment'
                "
              >
                <th colspan="3" class="text-center bg-light-blue">
                  CASUAL LEAVE
                </th>
                <th colspan="3" class="text-center bg-light-orange">
                  SICK LEAVE
                </th>
              </template>

              <th
                v-if="modalTitle === 'Leave Encashment'"
                rowspan="2"
                class="text-right"
              >
                ENCASHMENT
              </th>

              <template v-if="modalTitle === 'Advance & Loan'">
                <th colspan="3" class="text-center bg-light-green">ADVANCE</th>
                <th colspan="3" class="text-center bg-light-purple">LOAN</th>
              </template>
            </tr>
            <tr class="sub-header">
              <!-- Casual Leave -->
              <template
                v-if="
                  modalTitle === 'Remaining Leaves' ||
                  modalTitle === 'Leave Encashment'
                "
              >
                <th class="text-center bg-light-blue">ALLOWED</th>
                <th class="text-center bg-light-blue font-weight-bolder">
                  TAKEN
                </th>
                <th class="text-center bg-light-blue">BALANCE</th>

                <th class="text-center bg-light-orange">ALLOWED</th>
                <th class="text-center bg-light-orange font-weight-bolder">
                  TAKEN
                </th>
                <th class="text-center bg-light-orange">BALANCE</th>
              </template>

              <!-- Advance -->
              <template v-if="modalTitle === 'Advance & Loan'">
                <th class="text-right bg-light-green">TOTAL</th>
                <th class="text-right bg-light-green">RETURNED</th>
                <th class="text-right bg-light-green font-weight-bolder">
                  BALANCE
                </th>

                <th class="text-right bg-light-purple">TOTAL</th>
                <th class="text-right bg-light-purple">RETURNED</th>
                <th class="text-right bg-light-purple font-weight-bolder">
                  BALANCE
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in leavesData" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td class="text-center">
                {{ item.employeeCode || item.id || "-" }}
              </td>
              <td class="font-weight-bold">
                {{ item.staffName || item.name }}
              </td>
              <td>{{ item.fatherName || item.father_name || "-" }}</td>
              <td>{{ item.designation || "-" }}</td>

              <!-- Leaves Data -->
              <template
                v-if="
                  modalTitle === 'Remaining Leaves' ||
                  modalTitle === 'Leave Encashment'
                "
              >
                <td class="text-center">{{ item.clAllowed || 0 }}</td>
                <td class="text-center font-weight-bold text-primary">
                  <template v-if="showAbaLeaveTakenFormatting">
                    {{ abaBaseCasualTaken(item)
                    }}<span
                      v-if="abaExtraCasualTaken(item) > 0"
                      class="aba-extra-taken text-danger"
                    >
                      ({{ abaExtraCasualTaken(item) }})</span
                    >
                  </template>
                  <template v-else>{{ item.totalLeaves || 0 }}</template>
                </td>
                <td class="text-center">{{ item.clBalance || 0 }}</td>

                <td class="text-center">{{ item.slAllowed || 0 }}</td>
                <td class="text-center font-weight-bold text-warning">
                  <template v-if="showAbaLeaveTakenFormatting">
                    {{ abaBaseSickTaken(item)
                    }}<span
                      v-if="abaExtraSickTaken(item) > 0"
                      class="aba-extra-taken text-danger"
                    >
                      ({{ abaExtraSickTaken(item) }})</span
                    >
                  </template>
                  <template v-else>{{ item.sickLeaves || 0 }}</template>
                </td>
                <td class="text-center">{{ item.slBalance || 0 }}</td>
              </template>

              <!-- Encashment -->
              <td
                v-if="modalTitle === 'Leave Encashment'"
                class="text-right font-weight-bold text-success"
              >
                {{ formatCurrency(item.encashmentAmount || 0) }}
              </td>

              <!-- Advance & Loan Data -->
              <template v-if="modalTitle === 'Advance & Loan'">
                <td class="text-right">
                  {{ formatCurrency(item.totalAdvance || 0) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(item.advanceReturned || 0) }}
                </td>
                <td class="text-right font-weight-bold text-info">
                  {{
                    formatCurrency(
                      item.remainingAdvance != null
                        ? item.remainingAdvance
                        : item.advanceBalance || 0
                    )
                  }}
                </td>

                <td class="text-right">
                  {{ formatCurrency(item.totalLoan || 0) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(item.loanReturned || 0) }}
                </td>
                <td class="text-right font-weight-bold text-danger">
                  {{
                    formatCurrency(
                      item.remainingLoan != null
                        ? item.remainingLoan
                        : item.loanBalance || 0
                    )
                  }}
                </td>
              </template>
            </tr>

            <!-- If No Data -->
            <tr v-if="leavesData.length === 0">
              <td
                :colspan="emptyRowColspan"
                class="text-center py-4 text-muted"
              >
                No records found for the selected filters.
              </td>
            </tr>
          </tbody>
          <tfoot v-if="modalTitle === 'Leave Encashment'">
            <tr class="total-row">
              <td
                :colspan="encashmentFooterLabelColspan"
                class="text-right font-weight-bold"
              >
                TOTAL ENCASHMENT
              </td>
              <td class="text-right font-weight-bold bg-light">
                {{ formatCurrency(totalEncashment) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Footer -->
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "RemainingLeavesPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      leavesData: [],
      modalTitle: "",
      schoolLogo: null,
      campusName: "",
      campusBranch: "",
      db: "",
      cID: "",
      depID: 0,
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
    };
  },
  computed: {
    /** myskoolaba: TAKEN cells show base + red (extra) on Remaining Leaves & Leave Encashment */
    showAbaLeaveTakenFormatting() {
      const r = (this.reportDomain || "").toLowerCase().replace(/_/g, "");
      if (r !== "myskoolaba") return false;
      const t = this.modalTitle;
      return t === "Remaining Leaves" || t === "Leave Encashment";
    },
    emptyRowColspan() {
      let n = 5;
      if (
        this.modalTitle === "Remaining Leaves" ||
        this.modalTitle === "Leave Encashment"
      ) {
        n += 6;
        if (this.modalTitle === "Leave Encashment") n += 1;
      } else if (this.modalTitle === "Advance & Loan") {
        n += 6;
      }
      return Math.max(n, 5);
    },
    encashmentFooterLabelColspan() {
      return 11;
    },
    totalEncashment() {
      return this.leavesData.reduce(
        (sum, item) => sum + (parseFloat(item.encashmentAmount) || 0),
        0
      );
    },
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = this.pickRouteQueryParam(query, "db");
      this.cID = this.pickRouteQueryParam(query, "cID", "cId");
      const depRaw = this.pickRouteQueryParam(query, "depID");
      this.depID = depRaw !== undefined ? depRaw : 0;
      this.modalTitle = this.normalizeLeavesPrintTitle(query.title);
      this.reportDomain = (this.$store.state.report || "").replace(/_/g, "");

      this.campusName =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.branch) ||
        "My Skool";
      this.schoolLogo =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.logo) ||
        null;

      const missingDb =
        this.db === undefined ||
        this.db === null ||
        String(this.db).trim() === "";
      const missingCampus =
        this.cID === undefined || this.cID === null || this.cID === "";

      if (missingDb || missingCampus) {
        this.loading = false;
        this.error = "Invalid parameters. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    /**
     * Vue Router query values may be string or number; campus id 0 is valid — never use `a || b` here.
     */
    pickRouteQueryParam(query, ...keys) {
      if (!query || typeof query !== "object") return undefined;
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (!Object.prototype.hasOwnProperty.call(query, key)) continue;
        const v = query[key];
        if (v === undefined || v === null || v === "") continue;
        return v;
      }
      return undefined;
    },
    normalizeLeavesPrintTitle(raw) {
      const known = ["Remaining Leaves", "Leave Encashment", "Advance & Loan"];
      let s = "";
      if (raw == null || raw === "") {
        s = "";
      } else if (Array.isArray(raw)) {
        const first = raw.length ? raw[0] : "";
        s = first != null ? String(first).trim() : "";
      } else {
        s = String(raw).trim();
      }
      if (!s) return "Remaining Leaves";
      const match = known.find((t) => t.toLowerCase() === s.toLowerCase());
      return match || s;
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          timeout: 60000,
          params: {
            db: this.db,
            cId: this.cID,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          response.data.data &&
          response.data.data.length > 0
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          const info = match || response.data.data[0];

          this.campusName = info.name;
          this.campusBranch = this.cID == 0 ? "All Campuses" : info.branch;
          this.schoolLogo = info.logo;
        }
      } catch (e) {
        console.error("Failed to load campus info:", e);
      }
    },
    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;

        let apiEndpoint = "";
        if (this.modalTitle === "Advance & Loan") {
          apiEndpoint = "Staff/StaffLoansAndAdvanceReport";
        } else {
          apiEndpoint = "Staff/StaffRemainingLeavesReport";
        }

        const baseUrl = this.$store.state.domain;
        const url = `${baseUrl}${apiEndpoint}`;

        const params = {
          db: this.db,
          cID: this.cID,
          depID:
            this.depID === "0" || this.depID === 0 ? undefined : this.depID,
        };
        if (this.modalTitle === "Advance & Loan") {
          params.status = "present";
        }

        const response = await axios.get(url, {
          timeout: 180000,
          params,
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (response.data) {
          // If response has .data property, use it, otherwise use response.data directly if it's an array
          const rawData = response.data.data || response.data;
          if (Array.isArray(rawData)) {
            try {
              this.leavesData = this.filterLeavesData(rawData);
            } catch (processErr) {
              console.error("Failed to process report rows:", processErr);
              this.leavesData = [];
              this.error = "Could not process report data.";
            }
          } else {
            this.leavesData = [];
          }
        } else {
          this.error = "Failed to load report data.";
        }
      } catch (error) {
        console.error("Failed to load report:", error);
        const msg =
          error.code === "ECONNABORTED"
            ? "Request timed out. Try again or check your connection."
            : "Network error or server unavailable.";
        this.error = msg;
      } finally {
        this.loading = false;
      }
    },
    filterLeavesData(data) {
      if (!Array.isArray(data)) return [];

      let processedData = data;

      // Match the calculation logic from reports.vue
      if (
        this.modalTitle === "Remaining Leaves" ||
        this.modalTitle === "Leave Encashment"
      ) {
        processedData = data.map((item) => {
          const clAllowed = item.totalLeaves || 0;
          const usedCL = item.utilized_leaves || 0;
          const clBalance = Math.max(0, item.remaining_leaves || 0);

          const slAllowed = item.sickLeaves || 0;
          const usedSL = item.utilized_sickLeaves || 0;
          const slBalance = Math.max(0, item.remaining_sickLeaves || 0);

          const extraUtilizedLeaves =
            item.extraUtilizedLeaves != null
              ? Number(item.extraUtilizedLeaves)
              : item.extra_utilized_leaves != null
              ? Number(item.extra_utilized_leaves)
              : 0;
          const extraUtilizedSickLeaves =
            item.extraUtilizedSickLeaves != null
              ? Number(item.extraUtilizedSickLeaves)
              : item.extra_utilized_sick_leaves != null
              ? Number(item.extra_utilized_sick_leaves)
              : 0;

          return {
            ...item,
            employeeCode:
              item.employeeCode || item.id || item.employee_id || "N/A",
            staffName: item.staffName || item.name,
            fatherName: item.fatherName || item.father_name || "N/A",
            designation: item.designation || "N/A",
            clAllowed,
            totalLeaves: usedCL,
            clBalance,
            slAllowed,
            sickLeaves: usedSL,
            slBalance,
            remaining_leaves: clBalance,
            remaining_sickLeaves: slBalance,
            extraUtilizedLeaves,
            extraUtilizedSickLeaves,
          };
        });
      } else if (this.modalTitle === "Advance & Loan") {
        processedData = data.map((item) => {
          const totalAdvance = parseFloat(
            item.totalAdvance || item.advanceAmount || 0
          );
          const advanceReturned = parseFloat(item.advanceReturned || 0);
          const remainingAdvance = parseFloat(
            item.remainingAdvance != null
              ? item.remainingAdvance
              : item.advanceBalance != null
              ? item.advanceBalance
              : totalAdvance - advanceReturned
          );
          const totalLoan = parseFloat(item.totalLoan || item.loanAmount || 0);
          const loanReturned = parseFloat(item.loanReturned || 0);
          const remainingLoan = parseFloat(
            item.remainingLoan != null
              ? item.remainingLoan
              : item.loanBalance != null
              ? item.loanBalance
              : totalLoan - loanReturned
          );

          return {
            ...item,
            employeeCode:
              item.rfID ||
              item.employeeCode ||
              item.empCode ||
              item.id ||
              "N/A",
            staffName: item.staffName || item.name,
            fatherName: item.fatherName || item.father_name || "N/A",
            designation: item.designation || "N/A",
            totalAdvance,
            advanceReturned,
            advanceBalance: remainingAdvance,
            remainingAdvance,
            totalLoan,
            loanReturned,
            loanBalance: remainingLoan,
            remainingLoan,
          };
        });
      }

      // Keep staff who have any advance/loan activity (including fully repaid)
      if (this.modalTitle === "Advance & Loan") {
        return processedData.filter(
          (item) =>
            (parseFloat(item.totalAdvance) || 0) > 0 ||
            (parseFloat(item.totalLoan) || 0) > 0 ||
            (parseFloat(item.advanceBalance) || 0) > 0 ||
            (parseFloat(item.loanBalance) || 0) > 0
        );
      }

      return processedData;
    },
    formatCurrency(value) {
      if (value === undefined || value === null) return "0.00";
      return new Intl.NumberFormat("en-PK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    /** For myskoolaba Remaining Leaves & Leave Encashment: total taken includes extra; cell shows base + red (extra). */
    abaExtraCasualTaken(item) {
      const ex = Number(item.extraUtilizedLeaves);
      return Number.isFinite(ex) ? ex : 0;
    },
    abaBaseCasualTaken(item) {
      const total = Number(item.totalLeaves);
      const t = Number.isFinite(total) ? total : 0;
      return Math.max(0, t - this.abaExtraCasualTaken(item));
    },
    abaExtraSickTaken(item) {
      const ex = Number(item.extraUtilizedSickLeaves);
      return Number.isFinite(ex) ? ex : 0;
    },
    abaBaseSickTaken(item) {
      const total = Number(item.sickLeaves);
      const t = Number.isFinite(total) ? total : 0;
      return Math.max(0, t - this.abaExtraSickTaken(item));
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.remaining-leaves-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1400px;
  margin: 30px auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 80px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 1.7rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }
  }
}

.balance-summary {
  display: flex;
  justify-content: center;
  gap: 3rem;
  padding: 1.25rem;
  background: #f8f9fa;
  border-radius: 12px;
  margin: 1.5rem 0;
  border: 1px solid #eee;

  .balance-card {
    display: flex;
    flex-direction: column;
    align-items: center;

    .label {
      font-size: 0.8rem;
      font-weight: bold;
      color: #636e72;
      text-transform: uppercase;
      margin-bottom: 4px;
    }

    .value {
      font-size: 1.4rem;
      font-weight: 800;
    }
  }
}

.table-container {
  overflow-x: auto;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  font-size: 0.9rem;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 10px;
    vertical-align: middle;
  }

  thead {
    background: #2c3e50;
    color: white;

    th {
      font-weight: 700;
      text-transform: uppercase;
      font-size: 0.8rem;
      letter-spacing: 0.5px;
    }

    .sub-header th {
      background: #34495e;
      font-size: 0.75rem;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  .bg-light-blue {
    background-color: rgba(52, 152, 219, 0.1) !important;
    color: #2980b9 !important;
  }
  .bg-light-orange {
    background-color: rgba(230, 126, 34, 0.1) !important;
    color: #d35400 !important;
  }
  .bg-light-green {
    background-color: rgba(46, 204, 113, 0.1) !important;
    color: #27ae60 !important;
  }
  .bg-light-purple {
    background-color: rgba(155, 89, 182, 0.1) !important;
    color: #8e44ad !important;
  }
}

.signature-box {
  width: 200px;
  text-align: center;

  .sig-line {
    border-top: 1px solid #333;
    margin-bottom: 8px;
  }

  span {
    font-weight: 600;
    color: #636e72;
  }
}

.page-footer {
  color: #adb5bd;
}

.text-right {
  text-align: right;
}
.text-center {
  text-align: center;
}
.font-weight-bold {
  font-weight: 700;
}

.aba-extra-taken {
  font-weight: 700;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
  }

  .remaining-leaves-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .report-table {
    font-size: 9pt !important;

    th,
    td {
      padding: 5px !important;
    }
  }

  .aba-extra-taken {
    color: #dc3545 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .btn-modern,
  .control-bar {
    display: none !important;
  }
}
</style>
